import { jwtDecode } from 'jwt-decode';

/**
 * Who an access token belongs to, as one comparable string.
 *
 * Tabs on one origin share a single access-token slot, and a second user can log in in
 * another tab without the first one signing out — through a launch URL naming another
 * idp or login_hint, say. A refresh token must then never renew on behalf of whoever is
 * in the slot now: its grant would write the first user's token over the second one's
 * and silently switch every tab back. Every refresh token is therefore bound to the key
 * of the access token it arrived with, and checked against the slot before it is used.
 *
 * Tenants on different subdomains are different origins and never share a slot; the
 * tenant claim is in the key for the deployments that serve several from one host.
 * Not hashed: within one origin there is nothing to hide it from.
 */

const TENANT_CLAIM = 'http://schemas.invent.us/identity/claims/tenantid';

interface AccessTokenClaims {
  iss?: unknown;
  sub?: unknown;
  client_id?: unknown;
  azp?: unknown;
  aud?: unknown;
  [TENANT_CLAIM]?: unknown;
}

function asString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.filter((item) => typeof item === 'string').join(',');
  return '';
}

/**
 * The identity key of an access token, or `undefined` when it cannot be decoded or does
 * not say who it is. `undefined` never matches anything — not even another `undefined` —
 * so a token without an identity can only be refreshed by nobody else.
 */
export function identityKeyOf(accessToken: string | null | undefined): string | undefined {
  if (!accessToken) return undefined;

  let claims: AccessTokenClaims;
  try {
    claims = jwtDecode<AccessTokenClaims>(accessToken);
  } catch {
    return undefined;
  }

  const issuer = asString(claims.iss);
  const subject = asString(claims.sub);
  if (!issuer || !subject) return undefined;

  const client = asString(claims.client_id) || asString(claims.azp) || asString(claims.aud);
  return [issuer, subject, client, asString(claims[TENANT_CLAIM])].join('|');
}

/**
 * A renewal whose outcome no longer applies: the slot changed hands while it ran
 * (`identity_changed`), or was emptied by a signout (`signed_out`). Not a failure of the
 * session — the renewal circuit breaker must not count it, and nothing should fall back
 * to the frame because of it.
 */
export class RenewalSupersededError extends Error {
  readonly oidcError: 'identity_changed' | 'signed_out';
  readonly terminal = false;
  readonly superseded = true;

  constructor(reason: 'identity_changed' | 'signed_out') {
    super(reason);
    this.name = 'RenewalSupersededError';
    this.oidcError = reason;
  }
}

export function isRenewalSuperseded(error: unknown): error is RenewalSupersededError {
  return (error as { superseded?: unknown })?.superseded === true;
}
